import React from 'react';
import * as _ from 'lodash';
import Tippy from '../ReactTippy';

import {
  RAPIDA_FACE_IMAGE_RANGE_RESOLUTION,
  RAPIDA_FILE_MAX_SIZE,
} from '@bld/bld-libs/constants';
import { compressImage } from '@bld/bld-libs/utils';

import { AvatarDialog } from '../modals/AvatarDialog';
import CommonModal from '../modals/common/Modal';
import { LoadingModal } from '../modals/common/LoadingModal';
import { FormListSubNumberHeader } from '../CommonFormComponents';

import ImageUtility from '../../utilities/imageUtility';
import { isNullOrEmpty } from '../../utilities/stringUtil';

import { IMAGE_DESCRIPTION_OF_TITLE } from '../../utilities/constants';

import { TrashIcon } from '../icons/TrashIcon';
import { messageUtility } from '../../utilities/messageUtility';

/**
 * ファイルをアップロードする用コンポーネント
 * @props {boolean}showEditButton:編集ボタンを表示するか？
 * @props {boolean}showDeleteButton:編集ボタンを表示するか？
 * @props {string}imageUrl:画像URL
 * @props {string}imageName:画像名
 * @props {string}acceptType:許可する画像の拡張子 (複数許可したい場合はコンマ区切りで指定)
 * @props {string}tooltipText:ファイルを選択ボタンのTooltipメッセージ
 * @props {function}onFileUpDate:イメージファイルを更新した時の処理
 * @props {function}onFileDelete:イメージファイルを削除した時の処理
 */
export class FileUpload extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      file: undefined,
      isValidType: false,
      isValidSize: false,
      isValidRange: true,
      isLoading: false,
    };
    if (
      this.props.onFileUpDate !== 'undefined' &&
      typeof this.props.onFileUpDate === 'function'
    ) {
      this.onFileUpDate = this.props.onFileUpDate.bind(this);
    } else {
      console.warn('onFileUpDate is not function');
    }

    if (
      this.props.onFileDelete !== 'undefined' &&
      typeof this.props.onFileDelete === 'function'
    ) {
      this.onFileDelete = this.props.onFileDelete.bind(this);
    } else {
      console.warn('onFileDelete is not function');
    }

    if (this.props.info) {
      this.info = splitText(this.props.info);
    }
  }

  closeModal = showModal => {
    this.setState({ showModal, isLoading: false });
  };

  openAvatarDialog = async event => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      event.target.value = '';
      // ファイルサイズが10MBを超えていないことを確認
      const isValidSize = await ImageUtility.dynamicFileSizeCheck(
        file,
        RAPIDA_FILE_MAX_SIZE
      );

      // ファイルタイプがjpg/jpegであることを確認
      const isValidType = await ImageUtility.approvalFileType(file, ['jpg']);
      this.setState({
        isValidRange: true,
      });
      if (isValidSize && isValidType) {
        const srcImage = URL.createObjectURL(file);
        const { isValidRangeResolution } = await this.checkImageRange(srcImage);
        if (isValidRangeResolution) {
          this.setState({
            showModal: true,
          });
          compressImage(
            file,
            result => {
              this.setState({
                file: result,
                isValidType,
                isValidSize,
                isValidRangeResolution,
              });
            },
            RAPIDA_FACE_IMAGE_RANGE_RESOLUTION.maxSize,
          );
        } else {
          this.setState(
            {
              isValidRange: isValidRangeResolution,
            },
            () => {
              this.onFileUpDate(
                isValidSize,
                isValidType,
                isValidRangeResolution
              );
            }
          );
        }
      } else {
        this.onFileUpDate(isValidSize, isValidType);
      }
    }
  };

  resizeDataURL = (src, wantedWidth, wantedHeight) => {
    return new Promise(async function(resolve) {
      const img = document.createElement('img');
      img.src = src;
      img.onload = function() {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = wantedWidth;
        canvas.height = wantedHeight;
        ctx.drawImage(this, 0, 0, wantedWidth, wantedHeight);
        const dataURI = canvas.toDataURL('image/jpeg', 1.0);
        resolve(dataURI);
      };
    });
  };

  getImageResolution = async src => {
    return await new Promise(resolve => {
      const image = new Image();
      image.onload = () => {
        let obj = {
          width: image.naturalWidth,
          height: image.naturalHeight,
        };
        resolve(obj);
      };
      image.src = src;
    });
  };

  checkImageRange = async src => {
    const { width, height } = await this.getImageResolution(src);
    return {
      width,
      height,
      isValidRangeResolution:
        width >= RAPIDA_FACE_IMAGE_RANGE_RESOLUTION.minSize.width &&
        height >= RAPIDA_FACE_IMAGE_RANGE_RESOLUTION.minSize.height,
    };
  };

  validateImage = async src => {
    let imageBlob = await ImageUtility.changeImageDataToBlob(src);
    const imageUrl = await URL.createObjectURL(imageBlob);
    const { width, isValidRangeResolution } = await this.checkImageRange(
      imageUrl
    );
    const maxWidth = RAPIDA_FACE_IMAGE_RANGE_RESOLUTION.maxSize.width;
    const maxHeight = RAPIDA_FACE_IMAGE_RANGE_RESOLUTION.maxSize.height;
    if (width > maxWidth) {
      const resizeSrc = await this.resizeDataURL(imageUrl, maxWidth, maxHeight);
      imageBlob = await ImageUtility.changeImageDataToBlob(resizeSrc);
    }
    if (isValidRangeResolution) {
      this.closeModal(false);
    }
    this.setState(
      {
        isValidRange: isValidRangeResolution,
      },
      () => {
        this.onFileUpDate(
          this.state.isValidSize,
          this.state.isValidType,
          isValidRangeResolution,
          this.state.file.name,
          imageBlob,
          this.closeModal
        );
      }
    );
  };

  // 画面表示
  render() {
    const renderGateKeeperImageUploadItems = () => {
      return (
        <div className='ImageFileUpload__gatekeeper-right-items'>
          {_.map(IMAGE_DESCRIPTION_OF_TITLE, it => {
            return (
              <React.Fragment key={it.ID}>
                <FormListSubNumberHeader
                  number={it.TITLE_NUMBER}
                  title={it.TITLE}
                  rootClassName={
                    it.ID === IMAGE_DESCRIPTION_OF_TITLE.BASIC_REGISTER.ID &&
                    'image__sub-number-header'
                  }
                  noMarginTop={
                    it.ID === IMAGE_DESCRIPTION_OF_TITLE.BASIC_REGISTER.ID
                  }
                />
                {_.isNull(it.INFO) === false && (
                  <p className='image-info'>{it.INFO}</p>
                )}
                {it.ID !==
                IMAGE_DESCRIPTION_OF_TITLE.IMPORTANT_DESCRIPTION.ID ? (
                  <img
                    className='image-manual'
                    src={it.GATEKEEPER_IMAGE_SRC}
                    alt=''
                  />
                ) : (
                  <GuideLineText />
                )}
              </React.Fragment>
            );
          })}
        </div>
      );
    };

    const renderWorkerEditImageUploadItems = () => {
      return (
        <div className='ImageFileUpload__worker-right-items'>
          <div className='image-manual'>
            <FormListSubNumberHeader
              number={IMAGE_DESCRIPTION_OF_TITLE.BASIC_REGISTER.TITLE_NUMBER}
              title={IMAGE_DESCRIPTION_OF_TITLE.BASIC_REGISTER.TITLE}
              rootClassName='image__sub-number-header'
              noMarginTop
            />
            <img
              alt=''
              src={IMAGE_DESCRIPTION_OF_TITLE.BASIC_REGISTER.IMAGE_SRC}
            />
          </div>
        </div>
      );
    };

    const GuideLineText = () => {
      return (
        <div className='guide'>
          <p className='guide__text'>
            {messageUtility.getMessage('I-001-505')}
          </p>
        </div>
      );
    };

    return (
      <React.Fragment>
        <div
          className={`${
            this.props.isGateKeeperEdit
              ? 'ImageFileUpload__gatekeeper-left-items'
              : 'ImageFileUpload'
          } ${
            this.props.isWorkerEdit ? 'ImageFileUpload__worker-left-items' : ''
          }`}
        >
          {/* アップロードしたファイルを表示する */}
          <div className='img-box'>
            <div className='img-form-thumbnail'>
              {!isNullOrEmpty(this.props.imageUrl) && (
                <img src={this.props.imageUrl} alt={this.props.alt} />
              )}
            </div>

            {/* 削除ボタンを表示する */}
            {this.props.showDeleteButton && this.props.imageUrl && (
              <button
                className='btn-icon form-list__element--btn_delete'
                onClick={() => {
                  this.onFileDelete();
                }}
              >
                <TrashIcon className='icon--2_4x form-list__trash-icon' />
              </button>
            )}
          </div>

          {/* 編集ボタンを表示する */}
          {this.props.showEditButton && (
            <div className='operation-box'>
              <Tippy
                html={renderTooltipContent({
                  text: this.props.tooltipText,
                })}
                duration={10}
                distance={24}
                arrowType='sharp'
                delay={[500, 0]}
                placement='bottom'
                followCursor={true}
              >
                <label className='btn input-file'>
                  {data.title.fileSelectButton}
                  <input
                    type='file'
                    accept={this.props.acceptType}
                    value={_.defaultTo(this.props.imageName, '')}
                    onChange={async event => {
                      await this.openAvatarDialog(event);
                    }}
                  />
                </label>
              </Tippy>
              {this.props.info && (
                <div className='info'>
                  <span className='info__text'>{this.info}</span>
                </div>
              )}
              <CommonModal visible={this.state.showModal}>
                <AvatarDialog
                  file={this.state.file}
                  submit={avatarSrc => {
                    this.setState(
                      {
                        isLoading: true,
                        isValidRange: false,
                      },
                      async () => {
                        await this.validateImage(avatarSrc);
                      }
                    );
                  }}
                  close={() => {
                    this.closeModal(false);
                  }}
                  isValidRange={this.state.isValidRange}
                />
              </CommonModal>
              <LoadingModal visible={this.state.file && this.state.isLoading} />
            </div>
          )}
        </div>
        {this.props.isGateKeeperEdit && renderGateKeeperImageUploadItems()}
        {this.props.isWorkerEdit && renderWorkerEditImageUploadItems()}
      </React.Fragment>
    );
  }
}

const splitText = text => {
  return text.split('\\n').map(str => <p key={str}>{str}</p>);
};

// 定数
const data = {
  title: {
    fileSelectButton: 'ファイル選択',
  },
};

/**
 * ツールチップ
 * @param text {string}
 */
const renderTooltipContent = ({ text }) => {
  return (
    <React.Fragment>
      <div className='tippy-tooltip__text'>{text}</div>
    </React.Fragment>
  );
};



// WEBPACK FOOTER //
// ./src/components/image/FileUpload.jsx